import React, { useState, useEffect } from 'react';
import { Search, Package, MapPin, Clock, AlertCircle, Truck, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import { Booking } from '../types';
import { getOrderByNumber } from '../utils/orders';

export function TrackOrder() {
  const [orderNumber, setOrderNumber] = useState('');
  const [searchResult, setSearchResult] = useState<Booking | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset previous search results and errors
    setSearchResult(null);
    setError(null);
    
    // Validate input
    const trimmedOrderNumber = orderNumber.trim();
    if (!trimmedOrderNumber) {
      setError('Please enter an order number');
      return;
    }

    setIsSearching(true);

    try {
      const order = await getOrderByNumber(trimmedOrderNumber);

      if (order) {
        setSearchResult(order);
        setError(null);
      } else {
        setError('Order not found. Please check your order number and try again.');
        setSearchResult(null);
      }
    } catch (error) {
      console.error('Error searching for order:', error);
      setError('An error occurred while searching for your order. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const getStatusColor = (status: Booking['status']) => {
    switch (status) {
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'Confirmed Install Date':
        return 'bg-blue-100 text-blue-800';
      case 'Done':
        return 'bg-green-100 text-green-800';
      case 'Return/Refund':
        return 'bg-red-100 text-red-800';
      case 'Voided':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Done':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'Confirmed Install Date':
        return <Truck className="w-5 h-5 text-blue-600" />;
      case 'Return/Refund':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-600" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Track Your Order</h1>

          {/* Search Form */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <form onSubmit={handleSearch} className="space-y-4">
              <div>
                <label htmlFor="orderNumber" className="block text-sm font-medium text-gray-700 mb-2">
                  Enter your order number
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="orderNumber"
                    value={orderNumber}
                    onChange={(e) => {
                      setOrderNumber(e.target.value);
                      // Clear error when user starts typing
                      if (error) setError(null);
                    }}
                    placeholder="e.g., ORD-2024-001"
                    className={`block w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                      error 
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                    }`}
                    disabled={isSearching}
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Package className={`h-5 w-5 ${error ? 'text-red-400' : 'text-gray-400'}`} />
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="flex items-center text-sm text-red-600 mt-2 animate-fade-in">
                  <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                  <p>{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={!orderNumber.trim() || isSearching}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {isSearching ? (
                  <span className="flex items-center justify-center">
                    <Search className="animate-spin -ml-1 mr-2 h-5 w-5" />
                    Searching...
                  </span>
                ) : (
                  'Track Order'
                )}
              </button>
            </form>
          </div>

          {/* Order Details */}
          {searchResult && (
            <div className="bg-white rounded-lg shadow-md overflow-hidden animate-fade-in">
              {/* Order Summary */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Order {searchResult.orderNumber}
                  </h2>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(searchResult.status)}`}>
                    {searchResult.status}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Order Date</p>
                    <p className="font-medium">
                      {format(new Date(searchResult.createdAt), 'MMM d, yyyy')}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Installation Date</p>
                    <p className="font-medium">
                      {searchResult.deliverySchedule
                        ? format(new Date(searchResult.deliverySchedule.date), 'MMM d, yyyy')
                        : 'To be scheduled'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Product Details */}
              <div className="p-6 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Product Details</h3>
                <div className="flex items-start">
                  {searchResult.product.image && (
                    <img
                      src={searchResult.product.image}
                      alt={searchResult.product.name}
                      className="w-20 h-20 object-cover rounded-md"
                    />
                  )}
                  <div className="ml-4">
                    <p className="font-medium text-gray-900">{searchResult.product.name}</p>
                    <p className="text-sm text-gray-500">
                      {searchResult.product.brand} - {searchResult.product.type}
                    </p>
                    <p className="text-sm font-medium text-blue-600">
                      ${searchResult.product.price.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Status Timeline */}
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Status Updates</h3>
                <div className="space-y-8">
                  {searchResult.statusHistory.map((update, index) => (
                    <div key={index} className="relative">
                      {index !== searchResult.statusHistory.length - 1 && (
                        <div className="absolute top-5 left-4 -bottom-[calc(2rem+1px)] w-0.5 bg-gray-200" />
                      )}
                      <div className="relative flex items-start">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100">
                          {getStatusIcon(update.status)}
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-900">{update.status}</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {format(new Date(update.timestamp), 'MMM d, yyyy h:mm a')}
                          </p>
                          {update.note && (
                            <p className="mt-1 text-sm text-gray-600">{update.note}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Installation Details */}
              <div className="p-6 bg-gray-50">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Installation Details</h3>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <MapPin className="w-5 h-5 text-gray-400 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Installation Address</p>
                      <p className="text-sm text-gray-600">
                        {searchResult.deliveryAddress.street}<br />
                        {searchResult.deliveryAddress.city}, {searchResult.deliveryAddress.state} {searchResult.deliveryAddress.zipCode}
                      </p>
                    </div>
                  </div>
                  {searchResult.deliverySchedule && (
                    <div className="flex items-center">
                      <Clock className="w-5 h-5 text-gray-400 mr-2" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Time Slot</p>
                        <p className="text-sm text-gray-600">{searchResult.deliverySchedule.time}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Support Information */}
          <div className="mt-8 text-center">
            <p className="text-gray-600">
              Need help? Contact our support team at{' '}
              <a href="tel:1-800-DRYER" className="text-blue-600 hover:text-blue-500">
                1-800-DRYER
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}